import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import {
    CheckCircle, LinkSimple, PencilSimple, PaperPlaneTilt,
    Plus, Trash, X, User as UserIcon, ClockCounterClockwise, Copy,
} from "@phosphor-icons/react";

// V1.5.6.4 — visibility + validator helpers used across the recruiter Intake tab
// and the public candidate form. Kept identical in shape to the server-side
// evaluators in candidate_intake.py so what admin previews == what candidate sees.
export function isVisible(field, answers) {
    const cond = field?.show_if;
    if (!cond || typeof cond !== "object" || !cond.key) return true;
    let actual = answers?.[cond.key];
    let expected = cond.equals;
    if (typeof expected === "boolean" && typeof actual === "string") {
        actual = ["true", "yes", "y", "1"].includes(actual.trim().toLowerCase());
    }
    return actual === expected;
}

// Returns null when valid; a string error message when invalid.
export function validateFieldClient(field, value) {
    if (value === null || value === undefined || value === "") return null;
    for (const v of field?.validators || []) {
        if (!v || typeof v !== "object") continue;
        if (v.kind === "email") {
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value))) {
                return `${field.label} must be a valid email address.`;
            }
        } else if (v.kind === "phone_in") {
            const d = String(value || "").replace(/\D/g, "").replace(/^91/, "");
            if (!/^[6-9]\d{9}$/.test(d)) {
                return `${field.label} must be a valid 10-digit Indian mobile.`;
            }
        } else if (v.kind === "url") {
            if (!/^https?:\/\/[^\s/$.?#].[^\s]*$/i.test(String(value))) {
                return `${field.label} must be a valid URL (http/https).`;
            }
        } else if (v.kind === "min") {
            if (Number(value) < Number(v.value)) return `${field.label} must be ≥ ${v.value}.`;
        } else if (v.kind === "max") {
            if (Number(value) > Number(v.value)) return `${field.label} must be ≤ ${v.value}.`;
        } else if (v.kind === "regex" && v.pattern) {
            try {
                if (!new RegExp(v.pattern).test(String(value))) {
                    return v.message || `${field.label} has an invalid format.`;
                }
            } catch { /* invalid regex authored by admin — ignore */ }
        }
    }
    return null;
}

/**
 * IntakeTab — V1.5.6 generic screening intake form.
 * Rendered inside <ScreeningPanel /> as the first tab.
 *
 * States:
 *   1. loading — spinner-ish placeholder
 *   2. empty — no response yet; show 2-CTA card (Fill / Share)
 *   3. editing — inline form (recruiter fill)
 *   4. filled — read-only grid + Edit affordance
 *   5. share_pending — link generated, waiting for candidate self-fill
 */
export default function IntakeTab({ candidate, job, onCandidateChanged }) {
    const { user: me } = useAuth();
    const canEditFields = ["admin", "hiring_manager", "superadmin"].includes(me?.role);

    const [loading, setLoading] = useState(true);
    const [fields, setFields] = useState([]);
    const [response, setResponse] = useState(null);
    const [pendingShare, setPendingShare] = useState(false);
    const [mode, setMode] = useState("view"); // view | edit
    const [saving, setSaving] = useState(false);
    const [answers, setAnswers] = useState({});
    const [err, setErr] = useState("");
    const [shareUrl, setShareUrl] = useState("");
    const [sharing, setSharing] = useState(false);
    const [showAllOptional, setShowAllOptional] = useState(false);
    const [showAddField, setShowAddField] = useState(false);

    const load = useCallback(async () => {
        if (!candidate?.id) return;
        setLoading(true);
        try {
            const r = await api.get(`/candidates/${candidate.id}/intake`);
            setFields(r.data.fields || []);
            setResponse(r.data.response || null);
            setPendingShare(!!r.data.pending_share);
            setAnswers(((r.data.response || {}).answers) || {});
        } catch (ex) {
            setErr(formatApiError(ex.response?.data?.detail) || ex.message);
        } finally {
            setLoading(false);
        }
    }, [candidate?.id]);

    useEffect(() => { load(); }, [load]);

    const visibleFields = useMemo(
        () => fields.filter((f) => isVisible(f, answers)),
        [fields, answers],
    );
    const requiredFields = useMemo(
        () => visibleFields.filter((f) => f.required),
        [visibleFields],
    );
    const optionalFields = useMemo(
        () => visibleFields.filter((f) => !f.required),
        [visibleFields],
    );
    const missingRequired = useMemo(() => {
        return requiredFields
            .filter((f) => !isFilled(f, answers))
            .map((f) => f.label);
    }, [requiredFields, answers]);
    // V1.5.6.4 — per-field client-side validation errors so the recruiter
    // gets feedback before the server rejects on save. Keyed by field.key.
    const fieldErrors = useMemo(() => {
        const errs = {};
        for (const f of visibleFields) {
            const e = validateFieldClient(f, answers[f.key]);
            if (e) errs[f.key] = e;
        }
        return errs;
    }, [visibleFields, answers]);
    const hasFieldErrors = Object.keys(fieldErrors).length > 0;
    const isComplete = missingRequired.length === 0 && !hasFieldErrors;

    const startEdit = () => {
        setAnswers(((response || {}).answers) || {});
        setMode("edit");
        setErr("");
    };

    const cancelEdit = () => {
        setAnswers(((response || {}).answers) || {});
        setMode("view");
        setErr("");
    };

    const save = async () => {
        setErr("");
        setSaving(true);
        try {
            const r = await api.patch(`/candidates/${candidate.id}/intake`,
                                      { answers });
            setResponse(r.data.response);
            setAnswers(r.data.response.answers || {});
            setMode("view");
            toast.success("Intake form saved");
            onCandidateChanged?.({ ...candidate, intake_response: r.data.response });
        } catch (ex) {
            setErr(formatApiError(ex.response?.data?.detail) || ex.message);
        } finally {
            setSaving(false);
        }
    };

    const share = async ({ sendEmail = false } = {}) => {
        setErr("");
        setSharing(true);
        try {
            const r = await api.post(`/candidates/${candidate.id}/intake/share`,
                                     { send_email: sendEmail });
            setShareUrl(r.data.url);
            setPendingShare(true);
            if (sendEmail) {
                if (r.data.email_sent) {
                    toast.success(`Intake link emailed to ${candidate.email}`);
                } else if (r.data.email_error) {
                    toast.warning("Link generated — but email failed", {
                        description: r.data.email_error,
                    });
                } else {
                    toast.success("Intake link ready — copy below");
                }
            } else {
                toast.success("Intake link generated — copy or share it below");
            }
        } catch (ex) {
            setErr(formatApiError(ex.response?.data?.detail) || ex.message);
        } finally {
            setSharing(false);
        }
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            toast.success("Link copied to clipboard");
        } catch {
            toast.error("Copy failed — long-press the link to copy manually");
        }
    };

    const addCustomField = async (payload) => {
        try {
            const r = await api.post(`/jobs/${job.id}/intake-template/fields`, payload);
            setFields((prev) => [...prev, r.data]);
            setShowAddField(false);
            toast.success(`Added field · ${r.data.label}`);
        } catch (ex) {
            toast.error(formatApiError(ex.response?.data?.detail) || ex.message);
        }
    };

    const removeCustomField = async (key) => {
        if (!window.confirm("Remove this custom field for all candidates on this job?")) return;
        try {
            await api.delete(`/jobs/${job.id}/intake-template/fields/${key}`);
            setFields((prev) => prev.filter((f) => f.key !== key));
            toast.success("Field removed");
        } catch (ex) {
            toast.error(formatApiError(ex.response?.data?.detail) || ex.message);
        }
    };

    if (loading) {
        return (
            <div data-testid="intake-tab-loading"
                 className="border-2 border-cobalt/40 p-5 text-xs text-[#A3A3A3]">
                Loading intake form…
            </div>
        );
    }

    const filledByCandidate = response?.source === "candidate_self";

    return (
        <div data-testid="intake-tab" className="border-2 border-cobalt p-5">
            {/* Header */}
            <div className="flex items-start justify-between mb-3 gap-3">
                <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-cobalt">
                        Intake form · required
                    </p>
                    <p className="text-xs text-[#525252] mt-0.5">
                        {isComplete
                            ? "All required fields filled — candidate is ready to advance."
                            : `${missingRequired.length} required ${missingRequired.length === 1 ? "field" : "fields"} pending. Stage cannot advance until complete.`}
                    </p>
                </div>
                {response && mode === "view" && (
                    <button
                        data-testid="intake-edit-btn"
                        onClick={startEdit}
                        className="border border-[#E5E5E5] hover:border-ink text-[#525252] px-3 py-1.5 text-[10px] font-bold tracking-wider uppercase flex items-center gap-1 transition-colors shrink-0"
                    >
                        <PencilSimple size={10} weight="bold" /> Edit
                    </button>
                )}
            </div>

            {err && (
                <div data-testid="intake-error"
                     className="border border-[#DC2626] bg-[#FEF2F2] text-[#DC2626] text-xs px-3 py-2 mb-3">
                    {err}
                </div>
            )}

            {/* Empty state — 2 CTAs */}
            {!response && mode === "view" && !pendingShare && !shareUrl && (
                <div
                    data-testid="intake-empty-state"
                    className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                >
                    <button
                        data-testid="intake-fill-self-btn"
                        onClick={startEdit}
                        className="group border border-[#E5E5E5] hover:border-cobalt bg-[#F9FBFF] p-4 text-left transition-colors"
                    >
                        <div className="flex items-center gap-2 text-cobalt mb-1">
                            <UserIcon size={16} weight="fill" />
                            <p className="text-xs font-black uppercase tracking-[0.15em]">Fill it yourself</p>
                        </div>
                        <p className="text-[11px] text-[#525252] leading-relaxed">
                            Type the candidate's answers now. Recruiter-fill takes ~30 seconds.
                        </p>
                    </button>
                    <button
                        data-testid="intake-share-btn"
                        onClick={() => share({ sendEmail: false })}
                        disabled={sharing}
                        className="group border border-[#E5E5E5] hover:border-cobalt bg-white p-4 text-left transition-colors disabled:opacity-60"
                    >
                        <div className="flex items-center gap-2 text-ink mb-1">
                            <PaperPlaneTilt size={16} weight="fill" />
                            <p className="text-xs font-black uppercase tracking-[0.15em]">Send to candidate</p>
                        </div>
                        <p className="text-[11px] text-[#525252] leading-relaxed">
                            Share a private link — candidate fills the form. Single-use, expires in 7 days.
                        </p>
                        {candidate?.email && (
                            <span
                                role="button"
                                tabIndex={0}
                                data-testid="intake-share-email-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    share({ sendEmail: true });
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        share({ sendEmail: true });
                                    }
                                }}
                                className="inline-flex items-center gap-1 mt-2 text-[10px] font-bold uppercase tracking-wider text-cobalt hover:text-cobalt-dark cursor-pointer"
                            >
                                <PaperPlaneTilt size={10} weight="bold" />
                                Email it to {candidate.email.split("@")[0]}
                            </span>
                        )}
                    </button>
                </div>
            )}

            {/* Share link generated — show URL + copy */}
            {shareUrl && !response && (
                <div
                    data-testid="intake-share-link"
                    className="border border-cobalt/40 bg-[#F9FBFF] p-3 mb-3"
                >
                    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-cobalt mb-1.5">
                        <LinkSimple size={11} weight="bold" /> Intake link — copy &amp; send
                    </div>
                    <div className="flex gap-2 items-stretch">
                        <input
                            data-testid="intake-share-url-input"
                            readOnly
                            value={shareUrl}
                            className="flex-1 min-w-0 bg-white border border-[#E5E5E5] px-3 py-2 text-xs text-ink"
                        />
                        <button
                            data-testid="intake-copy-link-btn"
                            onClick={copyLink}
                            className="bg-cobalt hover:bg-cobalt-dark text-white px-3 py-2 text-[10px] font-bold tracking-wider uppercase inline-flex items-center gap-1"
                        >
                            <Copy size={11} weight="bold" /> Copy
                        </button>
                    </div>
                    <p className="text-[10px] text-[#525252] mt-2">
                        Link is single-use — it dies the moment the candidate submits.
                    </p>
                </div>
            )}

            {/* Pending share badge (link was generated in a prior session, no URL to display) */}
            {pendingShare && !shareUrl && !response && (
                <div
                    data-testid="intake-pending-share"
                    className="border border-amber-200 bg-amber-50 text-amber-900 p-3 mb-3 flex items-center gap-2 text-xs"
                >
                    <ClockCounterClockwise size={14} weight="fill" />
                    <div className="flex-1">
                        Waiting for candidate to fill the shared link.
                    </div>
                    {candidate?.email && (
                        <button
                            onClick={() => share({ sendEmail: true })}
                            disabled={sharing}
                            data-testid="intake-resend-email-btn"
                            className="border border-amber-300 hover:bg-amber-100 px-2 py-1 text-[10px] font-bold uppercase tracking-wider disabled:opacity-60"
                        >
                            Resend email
                        </button>
                    )}
                    <button
                        onClick={() => share({ sendEmail: false })}
                        disabled={sharing}
                        data-testid="intake-resend-link-btn"
                        className="border border-amber-300 hover:bg-amber-100 px-2 py-1 text-[10px] font-bold uppercase tracking-wider disabled:opacity-60"
                    >
                        New link
                    </button>
                </div>
            )}

            {/* Filled state — read-only grid */}
            {response && mode === "view" && (
                <>
                    {filledByCandidate && (
                        <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-100 inline-flex items-center gap-1 px-2 py-1 mb-3">
                            <CheckCircle size={11} weight="fill" /> Filled by candidate
                        </div>
                    )}
                    <FilledGrid
                        fields={showAllOptional ? fields : requiredFields}
                        answers={answers}
                    />
                    {optionalFields.length > 0 && (
                        <button
                            data-testid="intake-toggle-optional-btn"
                            onClick={() => setShowAllOptional((v) => !v)}
                            className="mt-3 text-[10px] font-bold uppercase tracking-wider text-cobalt hover:text-cobalt-dark"
                        >
                            {showAllOptional ? "Hide optional" : `Show all ${fields.length} fields`}
                        </button>
                    )}
                </>
            )}

            {/* Edit mode — inline form */}
            {mode === "edit" && (
                <>
                    <div className="space-y-3">
                        {fields.filter((f) => isVisible(f, answers)).map((f) => (
                            <FieldInput
                                key={f.key}
                                field={f}
                                value={answers[f.key]}
                                onChange={(v) => setAnswers((a) => ({ ...a, [f.key]: v }))}
                                canRemove={canEditFields && !f.builtin}
                                onRemove={() => removeCustomField(f.key)}
                                error={fieldErrors[f.key]}
                            />
                        ))}
                    </div>
                    {canEditFields && !showAddField && (
                        <button
                            onClick={() => setShowAddField(true)}
                            data-testid="intake-add-field-btn"
                            className="mt-3 text-[10px] font-black uppercase tracking-[0.15em] text-cobalt hover:text-cobalt-dark inline-flex items-center gap-1"
                        >
                            <Plus size={11} weight="bold" /> Add custom field
                        </button>
                    )}
                    {canEditFields && showAddField && (
                        <AddFieldRow
                            onCancel={() => setShowAddField(false)}
                            onAdd={addCustomField}
                        />
                    )}
                    <div className="flex items-center justify-between gap-3 pt-4 mt-4 border-t border-[#E5E5E5]">
                        <p className="text-xs text-[#525252]">
                            {isComplete ? (
                                <span className="text-emerald-700 font-bold inline-flex items-center gap-1">
                                    <CheckCircle size={12} weight="fill" /> All required filled
                                </span>
                            ) : (
                                <span className="text-amber-700 font-bold">
                                    {missingRequired.length} required left
                                </span>
                            )}
                        </p>
                        <div className="flex items-center gap-2">
                            {response && (
                                <button
                                    onClick={cancelEdit}
                                    data-testid="intake-cancel-btn"
                                    className="border border-[#E5E5E5] hover:border-ink text-[#525252] px-3 py-2 text-[10px] font-bold tracking-wider uppercase transition-colors"
                                >
                                    Cancel
                                </button>
                            )}
                            <button
                                onClick={save}
                                disabled={saving}
                                data-testid="intake-save-btn"
                                className="bg-cobalt hover:bg-cobalt-dark disabled:opacity-60 text-white px-4 py-2 text-[10px] font-bold tracking-wider uppercase inline-flex items-center gap-1 transition-colors"
                            >
                                <CheckCircle size={11} weight="fill" />
                                {saving ? "Saving…" : "Save intake"}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

// ------------------- helpers ------------------------------------------------
function isFilled(f, answers) {
    const v = answers?.[f.key];
    if (v === null || v === undefined) return false;
    if (f.type === "text" && !String(v).trim()) return false;
    if (f.type === "duration_ym") {
        if (!v || typeof v !== "object") return false;
        return v.years != null || v.months != null;
    }
    if (typeof v === "string" && !v.trim()) return false;
    return true;
}

function FilledGrid({ fields, answers }) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {fields.map((f) => (
                <div key={f.key} data-testid={`intake-view-${f.key}`}>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
                        {f.label} {f.unit ? <span className="text-[#A3A3A3]">({f.unit})</span> : null}
                    </p>
                    <p className="text-sm font-bold text-ink mt-0.5">
                        {formatValue(f, answers?.[f.key])}
                    </p>
                </div>
            ))}
        </div>
    );
}

function formatValue(f, v) {
    if (v == null || v === "") return <span className="text-[#A3A3A3] font-normal">—</span>;
    if (f.type === "boolean") return v ? "Yes" : "No";
    if (f.type === "number") return f.unit ? `${v} ${f.unit}` : String(v);
    if (f.type === "duration_ym") {
        const y = Number(v?.years || 0);
        const m = Number(v?.months || 0);
        if (!y && !m) return <span className="text-[#A3A3A3] font-normal">—</span>;
        const parts = [];
        if (y) parts.push(`${y}y`);
        if (m) parts.push(`${m}m`);
        return parts.join(" ");
    }
    return String(v);
}

function FieldInput({ field, value, onChange, canRemove, onRemove, error }) {
    const label = (
        <div className="flex items-center justify-between mb-1">
            <label className="text-[10px] font-black uppercase tracking-wider text-[#525252]">
                {field.label}
                {field.unit ? <span className="text-[#A3A3A3] normal-case ml-1">({field.unit})</span> : null}
                {field.required && <span className="text-[#DC2626] ml-1">*</span>}
                {!field.builtin && (
                    <span className="ml-2 text-[9px] font-bold uppercase text-cobalt bg-cobalt/10 px-1 py-0.5">
                        Custom
                    </span>
                )}
            </label>
            {canRemove && (
                <button
                    type="button"
                    onClick={onRemove}
                    data-testid={`intake-remove-field-${field.key}`}
                    className="text-[#A3A3A3] hover:text-[#DC2626] p-0.5"
                    aria-label={`Remove ${field.label}`}
                >
                    <Trash size={11} weight="bold" />
                </button>
            )}
        </div>
    );
    const commonClass = "w-full bg-white border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-cobalt";
    const errClass = error ? " border-[#DC2626] focus:border-[#DC2626]" : "";
    const inputClass = commonClass + errClass;

    const wrap = (children) => (
        <>
            {children}
            {error && (
                <p
                    data-testid={`intake-error-${field.key}`}
                    className="text-[10px] font-bold text-[#DC2626] mt-1"
                >
                    {error}
                </p>
            )}
        </>
    );

    if (field.type === "text") {
        return (
            <div data-testid={`intake-field-${field.key}`}>
                {label}
                {wrap(
                    <input
                        data-testid={`intake-input-${field.key}`}
                        type="text"
                        value={value ?? ""}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={field.hint || ""}
                        className={inputClass}
                    />
                )}
            </div>
        );
    }
    if (field.type === "number") {
        return (
            <div data-testid={`intake-field-${field.key}`}>
                {label}
                {wrap(
                    <input
                        data-testid={`intake-input-${field.key}`}
                        type="number"
                        step="0.01"
                        value={value ?? ""}
                        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
                        placeholder={field.hint || ""}
                        className={inputClass}
                    />
                )}
            </div>
        );
    }
    if (field.type === "select") {
        return (
            <div data-testid={`intake-field-${field.key}`}>
                {label}
                {wrap(
                    <select
                        data-testid={`intake-input-${field.key}`}
                        value={value ?? ""}
                        onChange={(e) => onChange(e.target.value || null)}
                        className={inputClass}
                    >
                        <option value="">— Select —</option>
                        {(field.options || []).map((o) => (
                            <option key={o} value={o}>{o}</option>
                        ))}
                    </select>
                )}
            </div>
        );
    }
    if (field.type === "boolean") {
        return (
            <div data-testid={`intake-field-${field.key}`}>
                {label}
                <div className="flex items-center gap-2">
                    {[
                        { v: true, l: "Yes" },
                        { v: false, l: "No" },
                    ].map((opt) => (
                        <button
                            key={opt.l}
                            type="button"
                            data-testid={`intake-input-${field.key}-${opt.l.toLowerCase()}`}
                            onClick={() => onChange(opt.v)}
                            className={`px-3 py-1.5 text-xs font-bold border transition-colors ${
                                value === opt.v
                                    ? "bg-cobalt text-white border-cobalt"
                                    : "border-[#E5E5E5] text-[#525252] hover:border-ink"
                            }`}
                        >
                            {opt.l}
                        </button>
                    ))}
                </div>
            </div>
        );
    }
    if (field.type === "date") {
        return (
            <div data-testid={`intake-field-${field.key}`}>
                {label}
                {wrap(
                    <input
                        data-testid={`intake-input-${field.key}`}
                        type="date"
                        value={value ?? ""}
                        onChange={(e) => onChange(e.target.value || null)}
                        className={inputClass}
                    />
                )}
            </div>
        );
    }
    if (field.type === "duration_ym") {
        const v = value || {};
        return (
            <div data-testid={`intake-field-${field.key}`}>
                {label}
                <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center border border-[#E5E5E5] focus-within:border-cobalt bg-white">
                        <input
                            data-testid={`intake-input-${field.key}-years`}
                            type="number"
                            min="0"
                            placeholder="0"
                            value={v.years ?? ""}
                            onChange={(e) => onChange({ ...v, years: e.target.value === "" ? null : Number(e.target.value) })}
                            className="w-full px-3 py-2 text-sm focus:outline-none"
                        />
                        <span className="pr-3 text-xs text-[#A3A3A3]">yrs</span>
                    </div>
                    <div className="flex items-center border border-[#E5E5E5] focus-within:border-cobalt bg-white">
                        <input
                            data-testid={`intake-input-${field.key}-months`}
                            type="number"
                            min="0"
                            max="11"
                            placeholder="0"
                            value={v.months ?? ""}
                            onChange={(e) => onChange({ ...v, months: e.target.value === "" ? null : Number(e.target.value) })}
                            className="w-full px-3 py-2 text-sm focus:outline-none"
                        />
                        <span className="pr-3 text-xs text-[#A3A3A3]">mo</span>
                    </div>
                </div>
            </div>
        );
    }
    return null;
}

function AddFieldRow({ onCancel, onAdd }) {
    const [label, setLabel] = useState("");
    const [type, setType] = useState("text");
    const [required, setRequired] = useState(false);
    const [unit, setUnit] = useState("");
    const [optionsRaw, setOptionsRaw] = useState("");
    const submit = () => {
        if (!label.trim()) return;
        const payload = {
            label: label.trim(),
            type,
            required,
            unit: unit.trim() || undefined,
        };
        if (type === "select") {
            payload.options = optionsRaw.split(",").map((s) => s.trim()).filter(Boolean);
            if (payload.options.length === 0) {
                toast.error("Add at least one option, comma-separated");
                return;
            }
        }
        onAdd(payload);
    };
    return (
        <div
            data-testid="intake-add-field-row"
            className="mt-3 border border-cobalt/40 bg-[#F9FBFF] p-3 space-y-2"
        >
            <div className="flex items-center justify-between">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt">
                    Add custom field
                </p>
                <button
                    onClick={onCancel}
                    className="p-0.5 hover:bg-white text-[#A3A3A3]"
                    aria-label="Cancel"
                    data-testid="intake-add-field-cancel"
                >
                    <X size={12} weight="bold" />
                </button>
            </div>
            <input
                data-testid="intake-new-field-label"
                type="text"
                placeholder="Field label — e.g. Notice period buyout offered"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                className="w-full bg-white border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-cobalt"
            />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <select
                    data-testid="intake-new-field-type"
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    className="bg-white border border-[#E5E5E5] px-3 py-2 text-xs focus:outline-none focus:border-cobalt"
                >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="select">Select (dropdown)</option>
                    <option value="boolean">Yes / No</option>
                    <option value="date">Date</option>
                    <option value="duration_ym">Duration (Y+M)</option>
                </select>
                <input
                    data-testid="intake-new-field-unit"
                    type="text"
                    placeholder="Unit (LPA, days, yrs…)"
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                    className="bg-white border border-[#E5E5E5] px-3 py-2 text-xs focus:outline-none focus:border-cobalt"
                />
                <label className="inline-flex items-center gap-2 text-xs text-[#525252] cursor-pointer px-2">
                    <input
                        data-testid="intake-new-field-required"
                        type="checkbox"
                        checked={required}
                        onChange={(e) => setRequired(e.target.checked)}
                        className="accent-cobalt"
                    />
                    Required
                </label>
            </div>
            {type === "select" && (
                <input
                    data-testid="intake-new-field-options"
                    type="text"
                    placeholder="Options, comma-separated — e.g. Yes, No, Maybe"
                    value={optionsRaw}
                    onChange={(e) => setOptionsRaw(e.target.value)}
                    className="w-full bg-white border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-cobalt"
                />
            )}
            <div className="flex items-center justify-end gap-2 pt-1">
                <button
                    onClick={onCancel}
                    data-testid="intake-new-field-cancel"
                    className="text-[10px] font-bold uppercase tracking-wider text-[#525252] hover:text-ink px-2 py-1"
                >
                    Cancel
                </button>
                <button
                    onClick={submit}
                    data-testid="intake-new-field-submit"
                    className="bg-cobalt hover:bg-cobalt-dark text-white text-[10px] font-bold tracking-wider uppercase px-3 py-1.5 inline-flex items-center gap-1"
                >
                    <Plus size={10} weight="bold" /> Add
                </button>
            </div>
        </div>
    );
}
